import { Controller, useFormContext } from 'react-hook-form';
import { Box } from 'enova-frontend-components';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Varmekilde } from '../../../../../utils/registerEnums';
import { CheckboxArea } from '../components/checkboxArea';
import { AboutFormControlProps } from '../../types';
import { OppvarmingVarmepumpe } from '../../../../../../../types/registration/userInput';

import { VarmepumpeFordelingRadio } from './varmepumpeFordelingRadio';
import { VarmekildeRadio } from './varmekildeRadio';
import { Punktoppvarming } from './punktoppvarming';
import { VannbarenVarmeVarmepumpe } from './vannbarenVarmeVarmepumpe';
import { Ventilasjon } from './ventilasjon';
import { updatedVarmefordeling } from './utils';

const hasVarmepumpeSelection = (varmepumpe: OppvarmingVarmepumpe | undefined): boolean => {
  return !!varmepumpe && (!!varmepumpe.punktoppvarming || !!varmepumpe.vannbarenVarme || !!varmepumpe.ventilasjon);
};

export const Varmepumpe: FC<AboutFormControlProps> = ({ control }) => {
  const { setValue } = useFormContext();
  const { t } = useTranslation();
  return (
    <Controller
      name="oppvarmingSelection.oppvarmingVarmepumpe"
      control={control}
      render={({ field: { value, onChange }, formState }) => {
        const error = formState.errors.oppvarming?.oppvarmingVarmepumpe;
        const fieldError = {
          type: 'custom',
          message: error?.message
        };

        return (
          <CheckboxArea
            error={error?.message ? fieldError : undefined}
            i18nPrefix="register.tekniskUtstyr.oppvarmingVarmepumpe"
            value={!!value}
            onChange={(checked: boolean) => {
              onChange(checked);

              const defaultValue: OppvarmingVarmepumpe = {
                varmeKilde: Varmekilde.Ukjent
              };
              // We set the default value if the checkbox is checked
              setValue(`oppvarming.oppvarmingVarmepumpe`, checked ? defaultValue : undefined);
            }}
          >
            <Controller
              name="oppvarming.oppvarmingVarmepumpe"
              control={control}
              rules={{
                validate: (value) => {
                  if (value?.varmeKilde === Varmekilde.Ukjent) {
                    return true;
                  }

                  if (!hasVarmepumpeSelection(value)) {
                    return t('register.errorMessage.field.oppvarmingVarmepumpe');
                  }

                  return true;
                }
              }}
              render={({ field: { value, onChange } }) => {
                const onVarmekildeChange = (varmekilde: Varmekilde) => {
                  // Use standardverdier if source is unknown
                  if (varmekilde === Varmekilde.Ukjent) {
                    onChange({
                      varmeKilde: varmekilde,
                      brukStandardVerdier: true
                    });
                    return;
                  }
                  onChange({ ...value, varmeKilde: varmekilde });
                };

                const varmekildeIsSelected = !!value?.varmeKilde && value?.varmeKilde !== Varmekilde.Ukjent;

                const isPunktoppvarming = !!value?.punktoppvarming;
                const isVannbarenVarme = !!value?.vannbarenVarme;
                const isVentilasjon = !!value?.ventilasjon;

                return (
                  <Box p={3} gap={3} display="flex" flexDirection="column">
                    <VarmekildeRadio value={value?.varmeKilde} onChange={onVarmekildeChange} />

                    {varmekildeIsSelected && (
                      <VarmepumpeFordelingRadio
                        value={value}
                        onChange={(varmefordeling) => {
                          onChange({
                            ...value,
                            ...updatedVarmefordeling(varmefordeling)
                          });
                        }}
                      />
                    )}
                    <Box px={5}>
                      {/* Display based on VarmepumpeFordelingRadio selection */}
                      {isPunktoppvarming && <Punktoppvarming control={control} />}
                      {isVannbarenVarme && <VannbarenVarmeVarmepumpe control={control} />}
                      {isVentilasjon && <Ventilasjon control={control} />}
                    </Box>
                  </Box>
                );
              }}
            />
          </CheckboxArea>
        );
      }}
    />
  );
};
